import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  ThemeIcon,
  Skeleton,
  Badge,
  Table,
  Button,
} from '@mantine/core';
import {
  IconLicense,
  IconUsers,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { licensesService } from '../services/api/licenses';
import { activationsService } from '../services/api/activations';
import { customersService } from '../services/api/customers';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../constants';
import dayjs from 'dayjs';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function StatCard({ title, value, icon, color, loading }: StatCardProps) {
  return (
    <Paper withBorder p="xl" radius="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          {loading ? (
            <Skeleton height={44} width={80} mt={4} />
          ) : (
            <Text fz={36} fw={700} lh={1.1}>
              {value}
            </Text>
          )}
        </Stack>
        <ThemeIcon color={color} variant="light" size={52} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: licenseStats, isLoading: licensesLoading } = useQuery({
    queryKey: ['licenses-stats'],
    queryFn: () => licensesService.getStats(),
  });

  const { data: expiringData, isLoading: expiringLoading } = useQuery({
    queryKey: ['licenses-expiring'],
    queryFn: () => licensesService.getExpiring(30),
  });

  const { data: activationsData, isLoading: activationsLoading } = useQuery({
    queryKey: ['activations-stats'],
    queryFn: () => activationsService.list({ limit: 10 }),
  });

  const { data: customerStats, isLoading: customersLoading } = useQuery({
    queryKey: ['customers-stats'],
    queryFn: () => customersService.getStats(),
  });

  const totalLicenses = licenseStats?.total || 0;
  const activeLicenses = licenseStats?.active || 0;
  const expiringLicenses = expiringData?.licenses?.length || 0;
  const recentActivations = activationsData?.activations || [];
  const totalCustomers = customerStats?.total || 0;

  return (
    <Container size="xl" py="md">
      <Title order={2} mb="lg">
        Dashboard
      </Title>

      <Grid mb="xl">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Licenses"
            value={totalLicenses}
            icon={<IconLicense size={26} />}
            color="blue"
            loading={licensesLoading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Licenses"
            value={activeLicenses}
            icon={<IconCheck size={26} />}
            color="green"
            loading={licensesLoading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Expiring (30 days)"
            value={expiringLicenses}
            icon={<IconAlertTriangle size={26} />}
            color="orange"
            loading={expiringLoading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Customers"
            value={totalCustomers}
            icon={<IconUsers size={26} />}
            color="violet"
            loading={customersLoading}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>
                Expiring Licenses (Next 30 Days)
              </Title>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => navigate('/licenses?expiring=30&trial=no')}
              >
                View All
              </Button>
            </Group>
            {expiringLoading ? (
              <Stack gap="xs">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} height={40} />
                ))}
              </Stack>
            ) : expiringData?.licenses?.length ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>EID</Table.Th>
                    <Table.Th>Customer</Table.Th>
                    <Table.Th>Expires</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {expiringData.licenses.slice(0, 10).map(license => (
                    <Table.Tr key={license.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {license.eid}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{license.customerName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="orange" variant="light">
                          {dayjs(license.termEnd).format('MMM D, YYYY')}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed" size="sm">
                No licenses expiring in the next 30 days
              </Text>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>
                Recent Activations
              </Title>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => navigate('/activations')}
              >
                View All
              </Button>
            </Group>
            {activationsLoading ? (
              <Stack gap="xs">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} height={40} />
                ))}
              </Stack>
            ) : recentActivations.length ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>EID</Table.Th>
                    <Table.Th>Platform</Table.Th>
                    <Table.Th>Activated</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recentActivations.slice(0, 10).map(activation => (
                    <Table.Tr key={activation.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {activation.eid}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={PLATFORM_COLORS[activation.platform]}
                          variant="light"
                          leftSection={PLATFORM_ICONS[activation.platform]}
                        >
                          {activation.platform}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {dayjs(activation.activatedAt).format('MMM D, h:mm A')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed" size="sm">
                No recent activations
              </Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
